/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { customElement, html, property } from 'lit-element';
import { repeat } from 'lit-html/directives/repeat';
import { getPeople, getPeopleFromGroup } from '../../graph/graph.people';
import { getUsersPresenceByPeople } from '../../graph/graph.presence';
import { getUsersForPeopleQueries, getUsersForUserIds } from '../../graph/graph.user';
import { Providers } from '../../Providers';
import { ProviderState } from '../../providers/IProvider';
import '../../styles/fabric-icon-font';
import { arraysAreEqual } from '../../utils/Utils';
import { MgtTemplatedComponent } from '../templatedComponent';
import { PersonCardInteraction } from './../PersonCardInteraction';
import { styles } from './mgt-people-css';
export { PersonCardInteraction } from './../PersonCardInteraction';
/**
 * web component to display a group of people or contacts by using their photos or initials.
 *
 * @export
 * @class MgtPeople
 * @extends {MgtTemplatedComponent}
 *
 * @cssprop --list-margin - {String} List margin for component
 * @cssprop --avatar-margin - {String} Margin for each person
 */
let MgtPeople = class MgtPeople extends MgtTemplatedComponent {
    constructor() {
        super();
        /**
         * Sets how the person-card is invoked
         * Set to PersonCardInteraction.none to not show the card
         *
         * @type {PersonCardInteraction}
         * @memberof MgtPerson
         */
        this.personCardInteraction = PersonCardInteraction.hover;
        this.showMax = 3;
    }
    /**
     * Array of styles to apply to the element. The styles should be defined
     * using the `css` tag function.
     */
    static get styles() {
        return styles;
    }
    /**
     * determines if agenda events come from specific group
     * @type {string}
     */
    get groupId() {
        return this._groupId;
    }
    set groupId(value) {
        if (this._groupId === value) {
            return;
        }
        this._groupId = value;
        this.requestStateUpdate(true);
    }
    /**
     * user id array
     *
     * @memberof MgtPeople
     */
    get userIds() {
        return this._userIds;
    }
    set userIds(value) {
        if (arraysAreEqual(this._userIds, value)) {
            return;
        }
        this._userIds = value;
        this.requestStateUpdate(true);
    }
    /**
     * allows developer to define queries of people for component
     * @type {string[]}
     */
    get peopleQueries() {
        return this._peopleQueries;
    }
    set peopleQueries(value) {
        if (arraysAreEqual(this._peopleQueries, value)) {
            return;
        }
        this._peopleQueries = value;
        this.requestStateUpdate(true);
    }
    /**
     * Request to reload the state.
     * Use reload instead of load to ensure loading events are fired.
     *
     * @protected
     * @memberof MgtBaseComponent
     */
    requestStateUpdate(force) {
        if (force) {
            this.people = null;
        }
        return super.requestStateUpdate(force);
    }
    /**
     * Invoked on each update to perform rendering tasks. This method must return
     * a lit-html TemplateResult. Setting properties inside this method will *not*
     * trigger the element to update.
     */
    render() {
        if (this.isLoadingState) {
            return this.renderLoading();
        }
        if (!this.people || this.people.length === 0) {
            return this.renderNoData();
        }
        return this.renderTemplate('default', { people: this.people, max: this.showMax }) || this.renderPeople();
    }
    /**
     * Render the loading state.
     *
     * @protected
     * @returns
     * @memberof MgtPeople
     */
    renderLoading() {
        return this.renderTemplate('loading', null) || html ``;
    }
    /**
     * Render the list of people.
     *
     * @protected
     * @param {*} people
     * @returns {TemplateResult}
     * @memberof MgtPeople
     */
    renderPeople() {
        const maxPeople = this.people.slice(0, this.showMax);
        return html `
      <ul class="people-list">
        ${repeat(maxPeople, p => p.id, p => html `
            <li class="people-person">
              ${this.renderPerson(p)}
            </li>
          `)}
        ${this.people.length > this.showMax ? this.renderOverflow() : null}
      </ul>
    `;
    }
    /**
     * Render the overflow content to represent any extra people, beyond the max.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPeople
     */
    renderOverflow() {
        const extra = this.people.length - this.showMax;
        return (this.renderTemplate('overflow', {
            extra,
            max: this.showMax,
            people: this.people
        }) ||
            html `
        <li class="overflow"><span>+${extra}<span></li>
      `);
    }
    /**
     * Render an individual person.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPeople
     */
    renderPerson(person) {
        let personPresence = {
            // set up default presence
            activity: 'Offline',
            availability: 'Offline',
            id: null
        };
        if (this.showPresence && this._peoplePresence) {
            personPresence = this._peoplePresence[person.id];
        }
        const avatarSize = 'small';
        return (this.renderTemplate('person', { person }, person.id) ||
            // set image to @ to flag the mgt-person component to
            // query the image from the graph
            html `
        <mgt-person
          .personDetails=${person}
          .fetchImage=${true}
          .avatarSize=${avatarSize}
          .personCardInteraction=${this.personCardInteraction}
          .showPresence=${this.showPresence}
          .personPresence=${personPresence}
        ></mgt-person>
      `);
    }
    /**
     * render the no data state.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPeople
     */
    renderNoData() {
        return this.renderTemplate('no-data', null) || html ``;
    }
    /**
     * load state into the component.
     *
     * @protected
     * @returns
     * @memberof MgtPeople
     */
    loadState() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.people) {
                const provider = Providers.globalProvider;
                if (provider && provider.state === ProviderState.SignedIn) {
                    const graph = provider.graph.forComponent(this);
                    // populate people
                    if (this.groupId) {
                        this.people = yield getPeopleFromGroup(graph, this.groupId);
                    }
                    else if (this.userIds) {
                        this.people = yield getUsersForUserIds(graph, this.userIds);
                    }
                    else if (this.peopleQueries) {
                        this.people = yield getUsersForPeopleQueries(graph, this.peopleQueries);
                    }
                    else {
                        this.people = yield getPeople(graph);
                    }
                    // populate presence for people
                    if (this.showPresence) {
                        this._peoplePresence = yield getUsersPresenceByPeople(graph, this.people);
                    }
                    else {
                        this._peoplePresence = null;
                    }
                }
            }
        });
    }
};
__decorate([
    property({
        attribute: 'group-id',
        type: String
    })
], MgtPeople.prototype, "groupId", null);
__decorate([
    property({
        attribute: 'user-ids',
        converter: (value, type) => {
            return value.split(',').map(v => v.trim());
        }
    })
], MgtPeople.prototype, "userIds", null);
__decorate([
    property({
        attribute: 'people',
        type: Object
    })
], MgtPeople.prototype, "people", void 0);
__decorate([
    property({
        attribute: 'people-queries',
        converter: (value, type) => {
            return value.split(',').map(v => v.trim());
        }
    })
], MgtPeople.prototype, "peopleQueries", null);
__decorate([
    property({
        attribute: 'show-max',
        type: Number
    })
], MgtPeople.prototype, "showMax", void 0);
__decorate([
    property({
        attribute: 'show-presence',
        type: Boolean
    })
], MgtPeople.prototype, "showPresence", void 0);
__decorate([
    property({
        attribute: 'person-card',
        converter: (value, type) => {
            value = value.toLowerCase();
            if (typeof PersonCardInteraction[value] === 'undefined') {
                return PersonCardInteraction.hover;
            }
            else {
                return PersonCardInteraction[value];
            }
        }
    })
], MgtPeople.prototype, "personCardInteraction", void 0);
MgtPeople = __decorate([
    customElement('mgt-people')
], MgtPeople);
export { MgtPeople };
//# sourceMappingURL=mgt-people.js.map